import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useBook } from "../lib/books";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import { translateGenre } from "../lib/book-display";
import { BookCover } from "../components/BookCover";
import "./book-detail.css";

export function BookDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const query = useBook(id);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  // We need a name for the admin reservation notification. Prompt for it at
  // checkout only when we don't already have one on file for this user.
  const needsName = Boolean(user) && !user?.firstName && !user?.lastName;

  const checkout = useMutation({
    mutationFn: (vars: { name?: string }) =>
      api<{ holdId: string; expiresAt: string; message: string }>(
        `/api/books/${id}/checkout`,
        { method: "POST", body: vars.name ? { name: vars.name } : {} },
      ),
    onSuccess: async () => {
      await Promise.all([
        refresh(),
        qc.invalidateQueries({ queryKey: ["books"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
      ]);
      navigate("/dashboard");
    },
    onError: (err: ApiError) => setError(err.message),
  });

  if (query.isPending) {
    return <div className="container"><div className="surface"><p>{t("common.loading")}</p></div></div>;
  }

  if (query.isError || !query.data) {
    return (
      <div className="container">
        <div className="surface" role="alert">
          <h1>{t("book.notFoundTitle")}</h1>
          <p>{t("book.notFoundBody")}</p>
          <Link to="/browse" className="btn btn-primary">{t("nav.browse")}</Link>
        </div>
      </div>
    );
  }

  const book = query.data.book;
  const isAvailable = book.status === "available";
  const hasEnoughPoints = (user?.points ?? 0) >= 1;

  return (
    <div className="container book-detail">
      <Link to="/browse" className="book-detail__back">{t("book.back")}</Link>

      <div className="book-detail__layout">
        <div className="book-detail__cover">
          <BookCover
            bookId={book.id}
            hasCover={Boolean(book.coverUrl)}
            title={book.title}
            author={book.author}
            alt={`${book.title}${book.author ? ` — ${book.author}` : ""}`}
          />
        </div>

        <div className="book-detail__info surface">
          <h1>{book.title}</h1>
          {book.author && <p className="book-detail__author">{book.author}</p>}

          <dl className="book-detail__meta">
            {book.genre && (<>
              <dt>{t("book.metaGenre")}</dt>
              <dd>{translateGenre(t, book.genre)}</dd>
            </>)}
            <dt>{t("book.metaAvailability")}</dt>
            <dd>
              <span className={`book-detail__status book-detail__status--${book.status}`}>
                {book.status === "available"
                  ? t("book.statusAvailable")
                  : book.status === "on_hold"
                    ? t("book.statusOnHold")
                    : t("book.statusCheckedOut")}
              </span>
            </dd>
            {book.isbn && (<>
              <dt>{t("book.metaIsbn")}</dt>
              <dd><code>{book.isbn}</code></dd>
            </>)}
          </dl>

          {book.description && (
            <>
              <h2>{t("book.aboutTitle")}</h2>
              <p>{book.description}</p>
            </>
          )}

          <div className="book-detail__actions">
            {!user ? (
              <>
                <Link to="/login" className="btn btn-primary">{t("book.loginToCheckout")}</Link>
                <p className="book-detail__hint">{t("book.checkoutHint")}</p>
              </>
            ) : !isAvailable ? (
              <p>{t("book.noLongerAvailable")}</p>
            ) : (
              <>
                {needsName && (
                  <div className="field">
                    <label htmlFor="checkout-name">{t("book.nameLabel")}</label>
                    <input
                      id="checkout-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={checkout.isPending}
                    />
                    <p className="book-detail__hint">{t("book.nameHint")}</p>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={checkout.isPending || !hasEnoughPoints}
                  onClick={() => {
                    setError(null);
                    const trimmed = name.trim();
                    if (needsName && trimmed.length < 2) {
                      setError(t("book.nameRequired"));
                      return;
                    }
                    checkout.mutate({ name: needsName ? trimmed : undefined });
                  }}
                >
                  {checkout.isPending ? t("book.checkoutSubmitting") : t("book.checkout")}
                </button>
                {!hasEnoughPoints && (
                  <p className="book-detail__hint" role="status">{t("book.needPoints")}</p>
                )}
                {error && <p className="field__error" role="alert">{error}</p>}
                <p className="book-detail__hint">{t("book.checkoutHint")}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
